import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { SHARED_LIBRARY_CONFIG } from 'projects/petszel-shared/src/public-api';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentfulService } from '../../service/contentful.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NationalbrandService } from '../../service/national-brands.service';
import {
  AnalyticsService,
  CtaTrackingData,
} from '../../service/google-analytics.service';
import { combineLatest, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NGXLogger } from 'ngx-logger';
import { ArticleUtilsService } from '../../service/article-utils.service';
import { ProductType, TagType } from '../../models/product.interface';
import { Store } from '@ngrx/store';
import { AppStateModel } from '../../shared-store/app-state.model';
import { selectAnimalId } from '../../shared-store/selected-variables/selected-variables.selectors';
import { selectSheltersExtended } from '../../shared-store/shelter/shelter.selectors';
import { selectOwner } from '../../shared-store/owner/owner.selectors';
import { selectPets } from '../../shared-store/pet/pet.selectors';
import { selectFeatureFlagTeleAdvice } from '../../shared-store/feature-flags/feature-flags.selector';
import { FeatureFlagModel } from '../../models/feature-flag.model';
import { ShelterExtendedModel } from '../../shared-store/shelter/shelter-extended.reducer';
import {
  AnimalResponse,
  BrandingResponse,
} from '../../models/branding.interface';
import { OwnerTasksService } from '../owner-dashboard/tasks/task.service';

@Component({
  selector: 'app-pet-article-page',
  templateUrl: './pet-article-page.component.html',
  styleUrls: ['./pet-article-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PetArticlePageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly destroyRef = inject(DestroyRef);
  private readonly tasksService = inject(OwnerTasksService);
  private readonly ngZone = inject(NgZone);

  @ViewChild('bodyHtmlContainer') bodyHtmlContainer!: ElementRef<HTMLElement>;
  @ViewChild('videoElement') videoElementRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('articleCompleteSentinel')
  articleCompleteSentinel!: ElementRef<HTMLElement>;

  showCompletionBanner = false;

  private completionObserver?: IntersectionObserver;
  private articleCompletionSent = false;
  slug!: any;
  entryId!: any;
  contentfulEntryData: any | null = null;
  bodyHtml!: string;
  title: string = '';
  subtitle: string = '';
  routerSubscription!: Subscription;
  cardsData: any[] = [];
  errorMessage: string = '';
  isLoadingContentfulData: boolean = true;
  ctaEndOfArticleImageAndText: any[] = [];
  filteredCTAs: any[] = [];
  filteredProductCTAs: any[] = [];
  showErrorBanner = false;
  legalName: string | null = null;
  ctaEndOfArticleProductAndService: any[] = [];
  daysSinceAdoption: number = 0;
  brandingDays: number = 0;
  shelterBrandingDays!: number;
  petLifeStages: string[] = [];
  petId: string = '';
  petInfo!: AnimalResponse;
  shelterInfo!: BrandingResponse;
  ownerInfo!: any;
  teleAdviceFeature!: FeatureFlagModel;

  teleAdviceTagId = 'brandTeleAdvicePetszel';
  private hasAppliedPageUtm = false;

  private managePetInfoSubs!: Subscription;
  private manageOtherSubs!: Subscription;
  private manageStoreSubs!: Subscription;

  trackByCard = (_: number, c: any) => c.slug || c.entryId || c.title;

  // ---- listener cleanup ----
  private unlistenBodyClick?: () => void;
  private unlistenVideoLoaded?: () => void;
  private unlistenCtaClick?: () => void;

  constructor(
    private route: ActivatedRoute,
    private contentfulService: ContentfulService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
    private brand: NationalbrandService,
    private renderer: Renderer2,
    private logger: NGXLogger,
    private router: Router,
    private articleUtilsService: ArticleUtilsService,
    private store: Store<AppStateModel>,
    @Inject(SHARED_LIBRARY_CONFIG) private config: any,
  ) {}

  ngOnInit(): void {
    this.scrollToTop();

    this.manageStoreSubs = combineLatest({
      selectedAnimalId: this.store.select(selectAnimalId),
      shelters: this.store.select(selectSheltersExtended),
      owner: this.store.select(selectOwner),
      teleAdvice: this.store.select(selectFeatureFlagTeleAdvice),
      params: this.route.paramMap,
      queryParams: this.route.queryParamMap,
      pets: this.store.select(selectPets),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.slug = state.params.get('id');
        const petIdParam = state.queryParams.get('animalId');

        if (this.slug) {
          this.petId = petIdParam || state.selectedAnimalId;
          this.petInfo = state.pets.find(
            (p) => p.id === this.petId,
          ) as AnimalResponse;
          this.ownerInfo = state.owner;
          this.teleAdviceFeature = state.teleAdvice as FeatureFlagModel;

          this.setShelter(state.shelters);
          this.getPetInfo();
          this.fetchContentfulEntryData(this.petId);

          this.isLoadingContentfulData = true;
          this.cdr.markForCheck();
        }
      });
  }

  private getArticleRichTextId(): string | null {
    return (
      this.contentfulEntryData?.entryId ||
      this.contentfulEntryData?.sys?.id ||
      null
    );
  }

  ngAfterViewInit(): void {
    this.logger.log('[ArticleComplete] ngAfterViewInit fired');
    this.logger.log(
      '[ArticleComplete] sentinel exists in ngAfterViewInit?',
      !!this.articleCompleteSentinel?.nativeElement,
    );
    this.logger.log(
      '[ArticleComplete] bodyHtml length in ngAfterViewInit:',
      this.bodyHtml?.length ?? 0,
    );

    this.cdr.detectChanges();
    this.listenToButtonClicks();

    if (this.videoElementRef?.nativeElement) {
      const video: HTMLVideoElement = this.videoElementRef.nativeElement;

      this.unlistenVideoLoaded?.();
      this.unlistenVideoLoaded = this.renderer.listen(
        video,
        'loadeddata',
        () => {
          let canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          let ctx = canvas.getContext('2d');
          ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
          let image = document.getElementById('stillFrame') as HTMLImageElement;
          if (image) {
            image.src = canvas.toDataURL();
            image.style.display = 'block';
          }
        },
      );
    }

    this.addClickTrackingToCTAs();

    this.logger.log(
      '[ArticleComplete] calling setupArticleCompletionObserver() from ngAfterViewInit...',
    );
    this.setupArticleCompletionObserver();
  }

  expertAdviceQueryParams(): { species?: string } {
    const conceptIds = this.getArticleTaxonomyConceptIds();
    const species = this.getArticleSpeciesForExpertAdvice();

    console.log('[ExpertAdviceQueryParams]', {
      articleTitle: this.contentfulEntryData?.title,
      rawConcepts: this.contentfulEntryData?.systemMetadata?.concepts,
      normalizedConceptIds: conceptIds,
      resolvedSpecies: species,
      queryParams: species ? { species } : {},
    });

    return species ? { species } : {};
  }

  private getArticleSpeciesForExpertAdvice(): string | null {
    const conceptIds = this.getArticleTaxonomyConceptIds();

    const hasDogConcept = conceptIds.some((id) => id.includes('dog'));
    const hasCatConcept = conceptIds.some((id) => id.includes('cat'));

    if (hasDogConcept && hasCatConcept) {
      return null;
    }

    if (hasDogConcept) {
      return 'dogs';
    }

    if (hasCatConcept) {
      return 'cats';
    }

    return null;
  }

  private getArticleTaxonomyConceptIds(): string[] {
    return (
      this.contentfulEntryData?.systemMetadata?.concepts
        ?.map((concept: { sys?: { id?: string } }) => concept.sys?.id)
        .filter((id: string | undefined): id is string => !!id)
        .map((id: string) => this.normalizeTaxonomyValue(id)) ?? []
    );
  }

  private normalizeTaxonomyValue(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, '');
  }
  private setupArticleCompletionObserver(): void {
    this.logger.log(
      '[ArticleComplete] setupArticleCompletionObserver() called',
    );

    if (!this.articleCompleteSentinel?.nativeElement) {
      this.logger.log('[ArticleComplete] sentinel not found, returning early');
      return;
    }

    const sentinelEl = this.articleCompleteSentinel.nativeElement;
    const rect = sentinelEl.getBoundingClientRect();
    this.logger.log('[ArticleComplete] sentinel found. rect:', rect);

    // Avoid double setup
    this.completionObserver?.disconnect();
    this.logger.log(
      '[ArticleComplete] previous observer disconnected (if existed)',
    );

    this.completionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (this.articleCompletionSent) return;

        this.articleCompletionSent = true;

        this.ngZone.run(() => {
          this.logger.log(
            '[ArticleComplete] ✅ sentinel hit → completing article...',
          );
          this.completeArticleIfEligible(); // API decides if banner shows
        });
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px 0px 600px 0px', // IMPORTANT: fire BEFORE user gets there
      },
    );

    this.completionObserver.observe(sentinelEl);
    this.logger.log('[ArticleComplete] observer is now observing sentinel ✅');
  }

  private completeArticleIfEligible(): void {
    const ownerId =
      this.petInfo?.petszelOwnerIds?.[
        this.petInfo.petszelOwnerIds.length - 1
      ] ?? null;

    const animalId = this.petInfo?.id ?? null;
    const articleRichTextId = this.getArticleRichTextId();

    this.logger.log(
      '[ArticleComplete] completeArticleIfEligible() called with:',
      {
        ownerId,
        animalId,
        articleRichTextId,
        petInfoLoaded: !!this.petInfo,
        contentfulLoaded: !!this.contentfulEntryData,
      },
    );

    if (!ownerId || !animalId || !articleRichTextId) {
      this.logger.log(
        '[ArticleComplete] Missing required IDs. Will allow retry later.',
      );
      this.logger.warn('[ArticleComplete] Missing required IDs', {
        ownerId,
        animalId,
        articleRichTextId,
      });

      // IMPORTANT: allow retry if observer fired too early
      this.articleCompletionSent = false;
      return;
    }

    const storageKey = `articleComplete:${ownerId}:${animalId}:${articleRichTextId}`;

    this.logger.log('[ArticleComplete] storageKey:', storageKey);
    this.logger.log(
      '[ArticleComplete] sessionStorage existing value:',
      sessionStorage.getItem(storageKey),
    );

    if (sessionStorage.getItem(storageKey) === '1') {
      this.ngZone.run(() => {
        this.logger.log(
          '[ArticleComplete] already completed this session → showing banner',
        );
        this.showCompletionBanner = true;
        this.cdr.markForCheck();
      });
      return;
    }

    this.logger.log(
      '[ArticleComplete] Making API call to completeArticleTask...',
    );
    this.tasksService
      .completeArticleTask(ownerId, animalId, articleRichTextId)
      .subscribe({
        next: () => {
          this.logger.log(
            '[ArticleComplete] API success - storing completion + showing banner',
          );
          sessionStorage.setItem(storageKey, '1');

          this.ngZone.run(() => {
            this.showCompletionBanner = true;
            this.cdr.detectChanges();
          });
        },
        error: (err) => {
          this.logger.log('[ArticleComplete] API error:', err);
          this.logger.error(
            '[ArticleComplete] Failed to complete article',
            err,
          );
          this.articleCompletionSent = false;
        },
      });
  }

  ngOnDestroy(): void {
    if (this.manageOtherSubs) this.manageOtherSubs.unsubscribe();
    if (this.manageStoreSubs) this.manageStoreSubs.unsubscribe();
    if (this.managePetInfoSubs) this.managePetInfoSubs.unsubscribe();
    if (this.routerSubscription) this.routerSubscription.unsubscribe();

    this.unlistenBodyClick?.();
    this.unlistenVideoLoaded?.();
    this.unlistenCtaClick?.();
    this.completionObserver?.disconnect();
  }

  // Method to replace {{sheltername}} with legalName
  replaceShelterName(text: string): string {
    if (!this.legalName) {
      this.logger.error('Legal name is not set, cannot replace shelter name.');
      return text;
    }
    const regex = new RegExp('{{sheltername}}', 'g');
    return text.replace(regex, this.legalName);
  }

  processContent(): void {
    if (this.legalName) {
      if (this.contentfulEntryData) {
        if (this.contentfulEntryData.title) {
          this.contentfulEntryData.title = this.replaceShelterName(
            this.contentfulEntryData.title,
          );
        }
        if (this.contentfulEntryData.subtitle) {
          this.contentfulEntryData.subtitle = this.replaceShelterName(
            this.contentfulEntryData.subtitle,
          );
        }
        if (this.bodyHtml) {
          this.bodyHtml = this.replaceShelterName(this.bodyHtml);
        }
        // Process ctaEndOfArticleImageAndText
        if (Array.isArray(this.ctaEndOfArticleImageAndText)) {
          this.ctaEndOfArticleImageAndText =
            this.ctaEndOfArticleImageAndText.map((ctaItem) => {
              if (ctaItem.ctaTextHtml) {
                ctaItem.ctaTextHtml = this.replaceShelterName(
                  ctaItem.ctaTextHtml,
                );
              }
              return ctaItem;
            });
        }
        // Process ctaEndOfArticleProductAndService
        if (Array.isArray(this.ctaEndOfArticleProductAndService)) {
          this.ctaEndOfArticleProductAndService =
            this.ctaEndOfArticleProductAndService.map((ctaItem) => {
              if (ctaItem.ctaTextHtml) {
                ctaItem.ctaTextHtml = this.replaceShelterName(
                  ctaItem.ctaTextHtml,
                );
              }
              return ctaItem;
            });
        }
        this.addClickTrackingToCTAs();
        this.cdr.markForCheck();
      }
    }
  }

  processCTAs(): void {
    const parser = new DOMParser();
    const doc = parser.parseFromString(this.bodyHtml, 'text/html');

    // Wrap inline CTA runs (2+ next to each other) into a grid container
    this.wrapCtaRunsInGrid(doc, '.ctaProductAndService');
    this.wrapCtaRunsInGrid(doc, '.ctaImageAndText');

    this.applyUtmToInlineAnchors(doc, 'Article');

    this.bodyHtml = doc.body.innerHTML;
    this.cdr.detectChanges();
  }

  checkDataAndProcessCTAs(): void {
    if (this.bodyHtml) {
      this.processContent();
      this.processCTAs();

      // Apply correct links to Teleadvice CTAs
      const ctaElements = Array.from(
        document.getElementsByClassName('ctaProductAndService'),
      );
      this.filterTeleAdviceInlineProducts(ctaElements);

      this.filteredProductCTAs = this.filterTeleAdviceProducts(
        this.filteredProductCTAs,
      );

      this.cdr.markForCheck();
    }
  }

  filterCTAs(): void {
    this.filteredCTAs = this.ctaEndOfArticleImageAndText;

    this.filteredProductCTAs = this.filterTeleAdviceProducts(
      this.ctaEndOfArticleProductAndService,
    );

    this.logger.log(
      '[FilterCTAs] Filtered Product CTAs:',
      this.filteredProductCTAs,
    );

    this.replaceBottomProductsPTags();
    this.attachTrackedLinksToBottomCTAs();
  }

  private attachTrackedLinksToBottomCTAs(): void {
    this.filteredCTAs = (this.filteredCTAs || []).map(
      (cta: any, index: number) => {
        const url = cta?.ctaUrl as string | undefined;

        const ctaHref = url
          ? this.withParams(
              url,
              this.buildUtmParams('ArticleEnd', {
                Product: cta?.productTitle ?? '',
              }),
            )
          : undefined;

        return {
          ...cta,
          ctaHref,
          ctaTrackingData: ctaHref
            ? this.buildArticleCtaTrackingData(cta, {
                location: 'article_end',
                type: 'card',
                link: ctaHref,
                text:
                  cta?.ctaButton?.ctaTextButton ||
                  cta?.ctaText ||
                  cta?.productTitle,
                position: index + 1,
              })
            : undefined,
        };
      },
    );

    this.filteredProductCTAs = (this.filteredProductCTAs || []).map(
      (cta: any, ctaIndex: number) => {
        const buildButton = (buttonIndex: 1 | 2 | 3) => {
          const url = cta?.[`ctaUrl${buttonIndex}`] as string | undefined;
          const button = cta?.[`ctaButton${buttonIndex}`] as
            | { ctaTextButton?: string }
            | undefined;

          if (!url || !button) {
            return undefined;
          }

          const link = this.withParams(
            url,
            this.buildUtmParams('ArticleEnd', {
              Button: String(buttonIndex),
              Product: cta?.productTitle ?? '',
            }),
          );

          return {
            link,
            tracking: this.buildArticleCtaTrackingData(cta, {
              location: 'article_end',
              type: 'button',
              link,
              text: button.ctaTextButton || cta?.productTitle,
              position: ctaIndex + 1,
              buttonIndex,
            }),
          };
        };

        const button1 = buildButton(1);
        const button2 = buildButton(2);
        const button3 = buildButton(3);

        return {
          ...cta,
          ctaHref1: button1?.link,
          ctaHref2: button2?.link,
          ctaHref3: button3?.link,
          ctaTrackingData1: button1?.tracking,
          ctaTrackingData2: button2?.tracking,
          ctaTrackingData3: button3?.tracking,
        };
      },
    );
  }

  private buildArticleCtaTrackingData(
    cta: any,
    options: {
      location: 'article_inline' | 'article_end';
      type: 'banner' | 'button' | 'link' | 'card';
      link: string;
      text?: string;
      position: number;
      buttonIndex?: number;
    },
  ): CtaTrackingData {
    const contentfulEntryId = String(
      cta?.entryId || cta?.sys?.id || cta?.contentfulEntryId || '',
    );

    const sourceContentId = String(
      this.contentfulEntryData?.entryId ||
        this.contentfulEntryData?.sys?.id ||
        this.slug ||
        'article',
    );

    const stableIdentifier =
      contentfulEntryId ||
      `${sourceContentId}_${options.location}_${options.position}`;

    const buttonSuffix = options.buttonIndex
      ? `_button_${options.buttonIndex}`
      : '';

    return {
      cta_id: this.toAnalyticsName(
        `article_detail_${stableIdentifier}${buttonSuffix}`,
      ),
      cta_product_name:
        cta?.ctaInternalName || cta?.productTitle || cta?.title || undefined,
      cta_type: options.type,
      source_screen: 'article_detail',
      cta_location: options.location,
      cta_position: options.position,
      destination_screen: this.resolveDestinationScreen(options.link),
      cta_link: this.normalizeAnalyticsLink(options.link),
      cta_text: options.text?.trim() || undefined,
      source_content_id: sourceContentId,
      source_content_slug:
        this.contentfulEntryData?.slug || this.slug || undefined,
      source_content_type: 'article',
      cta_variant: 'control',
      pet_species: this.petInfo?.animalType?.toLowerCase() || undefined,
    };
  }

  private toAnalyticsName(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private normalizeAnalyticsLink(link: string): string {
    try {
      const url = new URL(link, window.location.origin);

      if (url.origin === window.location.origin) {
        return url.pathname;
      }

      return `${url.origin}${url.pathname}`;
    } catch {
      return link.split('?')[0];
    }
  }

  private resolveDestinationScreen(link: string): string {
    const normalized = link.toLowerCase();

    if (normalized.includes('/insurance')) {
      return 'insurance';
    }

    if (
      normalized.includes('/providers') ||
      normalized.includes('teleadvice')
    ) {
      return 'browse_providers';
    }

    if (normalized.includes('/medical')) {
      return 'pet_records';
    }

    if (normalized.includes('/articles/') || normalized.includes('/video')) {
      return 'article_detail';
    }

    return 'external_product';
  }

  utmQP(placement: string, extra: Record<string, string> = {}) {
    return this.buildUtmParams(placement, extra);
  }

  private addClickTrackingToCTAs(): void {
    const container = this.bodyHtmlContainer?.nativeElement;

    if (!container) {
      return;
    }

    this.unlistenCtaClick?.();

    this.unlistenCtaClick = this.renderer.listen(
      container,
      'click',
      (event: Event) => {
        const target = event.target;

        if (!(target instanceof Element)) {
          return;
        }

        const clickable = target.closest(
          '.ctaProductAndService a, ' +
            '.ctaImageAndText a, ' +
            '.ctaProductAndService input[type="button"], ' +
            '.ctaImageAndText input[type="button"]',
        ) as HTMLAnchorElement | HTMLInputElement | null;

        if (!clickable) {
          return;
        }

        const ctaElement = clickable.closest(
          '.ctaProductAndService, .ctaImageAndText',
        ) as HTMLElement | null;

        if (!ctaElement) {
          return;
        }

        const ctaElements = Array.from(
          container.querySelectorAll<HTMLElement>(
            '.ctaProductAndService, .ctaImageAndText',
          ),
        ).filter((element) => !element.classList.contains('hidden'));

        const position = Math.max(ctaElements.indexOf(ctaElement) + 1, 1);

        const anchor =
          clickable instanceof HTMLAnchorElement
            ? clickable
            : ctaElement.querySelector<HTMLAnchorElement>('a[href]');

        const link = anchor?.href;

        if (!link) {
          return;
        }

        const productName =
          ctaElement.querySelector('.cta_product_name')?.textContent?.trim() ||
          undefined;

        const ctaText =
          clickable instanceof HTMLInputElement
            ? clickable.value
            : clickable.textContent?.trim();

        this.analyticsService.trackCtaClick(
          this.buildArticleCtaTrackingData(
            {
              productTitle: productName,
            },
            {
              location: 'article_inline',
              type: clickable instanceof HTMLInputElement ? 'button' : 'link',
              link,
              text: ctaText,
              position,
            },
          ),
        );
      },
    );
  }

  private wrapCtaRunsInGrid(
    doc: Document,
    selector: string,
    gridClass = 'cta-grid',
  ): void {
    const all = Array.from(doc.querySelectorAll(selector));
    if (all.length < 2) return;

    const parents = new Set<Element>();
    all.forEach((el) => el.parentElement && parents.add(el.parentElement));

    parents.forEach((parent) => {
      const children = Array.from(parent.children);

      let run: Element[] = [];
      const flush = () => {
        const visibleRun = run.filter((el) => !el.classList.contains('hidden'));
        if (visibleRun.length >= 2) {
          const grid = doc.createElement('div');
          grid.className = gridClass;

          visibleRun[0].before(grid);
          visibleRun.forEach((el) => grid.appendChild(el));
        }
        run = [];
      };

      for (const child of children) {
        if (child.matches(selector)) {
          run.push(child);
        } else {
          flush();
        }
      }
      flush();
    });
  }

  getSafeHtml(): SafeHtml {
    let bodyString = this.filterInlineProductsByLifeStage();
    bodyString = this.replaceContent(bodyString);
    return this.sanitizer.bypassSecurityTrustHtml(bodyString);
  }

  fetchContentfulEntryData(petszelAnimalId: string): void {
    const idType = this.determineIdType(this.slug);

    this.contentfulService
      .getEntryById(
        this.slug,
        petszelAnimalId,
        idType,
        this.petInfo?.petszelOwnerIds?.[
          this.petInfo.petszelOwnerIds.length - 1
        ],
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entryData: any) => {
          this.contentfulEntryData = entryData;

          this.ensurePageUtms('Article');
          this.processContent();

          const productAndServiceCTAs =
            entryData?.ctaEndOfArticleProductAndService;
          if (productAndServiceCTAs) {
            this.ctaEndOfArticleProductAndService = productAndServiceCTAs;
          }

          this.logger.log(
            '[CF] Raw End-of-Article Product CTAs:',
            this.ctaEndOfArticleProductAndService,
          );

          const imageAndTextCTAs = entryData?.ctaEndOfArticleImageAndText;
          if (imageAndTextCTAs) {
            this.ctaEndOfArticleImageAndText = imageAndTextCTAs;
          } else {
            this.logger.warn('[CF] No ctaEndOfArticleImageAndText on entry');
          }

          this.attachTrackedLinksToBottomCTAs();

          this.filterCTAs();
          this.processContent();
          this.processCTAs();

          this.fetchOtherData();
          this.populateCardsData();

          this.isLoadingContentfulData = false;
          this.checkDataAndProcessCTAs();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isLoadingContentfulData = false;
          this.showErrorBanner = true;
          this.logger.error('Failed to load Contentful entry', err);
          this.cdr.markForCheck();
        },
      });
  }

  populateCardsData(): void {
    this.cardsData = [];

    const hasArticles =
      Array.isArray(this.contentfulEntryData?.relevantArticlesRef) &&
      this.contentfulEntryData.relevantArticlesRef.length > 0;

    const hasVideos =
      Array.isArray(this.contentfulEntryData?.relevantVideosRef) &&
      this.contentfulEntryData.relevantVideosRef.length > 0;

    // Articles → cards
    if (hasArticles) {
      const articlesData = this.contentfulEntryData!.relevantArticlesRef.map(
        (articleRef: any) => {
          if (!articleRef) return null;
          return {
            type: 'article' as const,
            title: articleRef.title || '',
            subtitle: articleRef.subtitle || '',
            image: articleRef.articleHeroImageSimple?.file?.url || '',
            entryId: articleRef.entryId || '',
            slug: articleRef.slug || '',
            sponsored: !!articleRef.sponsored,
          };
        },
      ).filter(Boolean);

      this.cardsData = this.cardsData.concat(articlesData as any[]);
    }

    // Videos → cards
    if (hasVideos) {
      const videosData = this.contentfulEntryData!.relevantVideosRef.map(
        (videoRef: any) => {
          if (!videoRef) return null;
          return {
            type: 'video' as const,
            title: videoRef.videoName || '',
            subtitle: videoRef.videoDescription || '',
            video: videoRef.videoAssetSimple?.file?.url || '',
            entryId: videoRef.entryId || '',
            slug: videoRef.slug || '',
            videoLayout: videoRef.videoLayout,
            videoThumbnail: videoRef.videoThumbnailSimple?.file?.url || '',
            authorName:
              videoRef.videoAuthor?.publicauthorName || 'Unknown Author',
            sponsored: !!videoRef.sponsored,
          };
        },
      ).filter(Boolean);

      this.cardsData = this.cardsData.concat(videosData as any[]);
    }

    if (!hasArticles && !hasVideos) {
      this.logger.error(
        'Both relevantArticlesRef and relevantVideosRef are undefined, null, or empty.',
      );
    }

    this.cdr.markForCheck();
  }

  scrollToTop(): void {
    document.documentElement.scrollTop = 0;
    // This works on Safari
    document.body.scrollTop = 0;
  }

  fetchOtherData(): void {
    const idType = this.determineIdType(this.slug);

    this.contentfulService
      .getContent(
        this.slug,
        idType,
        this.petInfo.id,
        this.petInfo.petszelOwnerIds[this.petInfo.petszelOwnerIds.length - 1],
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.data.bodyHtml) {
            this.bodyHtml = this.processHtmlContent(data.data.bodyHtml);
            this.logger.log(
              '[ArticleComplete] fetchOtherData bodyHtml set. length:',
              this.bodyHtml.length,
            );

            this.checkDataAndProcessCTAs();

            setTimeout(() => {
              this.logger.log(
                '[ArticleComplete] re-calling setupArticleCompletionObserver() after bodyHtml render',
              );
              this.logger.log(
                '[ArticleComplete] sentinel exists AFTER bodyHtml?',
                !!this.articleCompleteSentinel?.nativeElement,
              );

              const el = this.articleCompleteSentinel?.nativeElement;
              if (el) {
                const rect = el.getBoundingClientRect();
                this.logger.log('[ArticleComplete] sentinel rect:', rect);
              }

              this.setupArticleCompletionObserver();
            }, 0);
          } else {
            this.logger.log(
              '[ArticleComplete] fetchOtherData: bodyHtml missing/empty',
            );
            this.logger.error('bodyHtml is undefined or empty.');
            this.bodyHtml = '';
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.logger.error('Error in fetchOtherData:', err);
          this.showErrorBanner = true;
          this.cdr.markForCheck();
        },
      });
  }

  private ensurePageUtms(placement: string = 'PageView'): void {
    if (this.hasAppliedPageUtm) return;

    const want = this.buildUtmParams(placement);
    const cur = new URL(window.location.href);

    const missingOrDiff = Object.entries(want).some(
      ([k, v]) => cur.searchParams.get(k) !== v,
    );

    if (!missingOrDiff) {
      this.hasAppliedPageUtm = true;
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: want,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.hasAppliedPageUtm = true;
  }

  determineIdType(id: string): 'slug' | 'entry' {
    // Regex to check if the ID is in slug format (contains hyphens)
    const slugPattern = /-/;
    // If the ID contains hyphens, we assume it's a slugId, otherwise it's an entryId
    return slugPattern.test(id) ? 'slug' : 'entry';
  }

  private withParams(url: string, params: Record<string, string>): string {
    try {
      const u = new URL(url, window.location.origin);
      Object.entries(params).forEach(([k, v]) => {
        if (v == null || v === '') return;
        u.searchParams.delete(k);
        u.searchParams.append(k, v);
      });
      return u.toString();
    } catch {
      return url;
    }
  }

  private applyUtmToInlineAnchors(doc: Document, placement = 'Article'): void {
    const params = this.buildUtmParams(placement);
    const anchors = doc.querySelectorAll('a[href]');
    anchors.forEach((a) => {
      const before = a.getAttribute('href')!;
      const after = this.withParams(before, params);
      a.setAttribute('href', after);
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener');
    });
  }

  private buildUtmParams(placement: string, extra?: Record<string, string>) {
    const shelterId = String(
      this.shelterInfo?.accountId ?? this.petInfo?.shelterAccountId ?? '',
    );
    const species = this.petInfo?.animalType ?? '';
    const campaign = this.contentfulEntryData?.slug || this.slug || '';
    return {
      utm_source: 'Petszel',
      utm_medium: 'WebApp',
      utm_campaign: campaign,
      Shelter_ID: shelterId,
      Species: species,
      Placement: placement,
      ...(extra ?? {}),
    };
  }

  processHtmlContent(rawHtml: string): string {
    // Process the HTML string as needed
    let processedHtml = rawHtml;

    // Replace {{sheltername}} with legalName
    if (this.legalName) {
      processedHtml = processedHtml.replace(/{{sheltername}}/g, this.legalName);
    }

    const parser = new DOMParser();
    let doc = parser.parseFromString(processedHtml, 'text/html');

    // Manipulate parsed HTML (e.g., adding classes to img or a tags)
    const imgElements = doc.querySelectorAll('img');
    imgElements.forEach((imgElement) => {
      if (imgElement.getAttribute('src') === '') {
        imgElement.classList.add('no-image');
      } else {
        imgElement.classList.add('custom-image-class');
      }
    });

    const aElements = doc.querySelectorAll('a');
    aElements.forEach((aElement) => {
      aElement.setAttribute('target', '_blank');
    });

    this.applyUtmToInlineAnchors(doc, 'Article');

    return doc.body.innerHTML;
  }

  listenToButtonClicks(): void {
    const container = this.bodyHtmlContainer?.nativeElement;

    if (!container) {
      return;
    }

    this.unlistenBodyClick?.();

    this.unlistenBodyClick = this.renderer.listen(
      container,
      'click',
      (event: Event) => {
        const target = event.target;

        if (!(target instanceof HTMLElement)) {
          return;
        }

        if (target.closest('.ctaProductAndService, .ctaImageAndText')) {
          return;
        }

        if (this.isTrackableButton(target)) {
          this.trackButtonClick(target as HTMLInputElement);
        }
      },
    );
  }

  private isTrackableButton(element: HTMLElement): boolean {
    return (
      element.tagName.toLowerCase() === 'input' &&
      element.getAttribute('type') === 'button'
    );
  }

  private trackButtonClick(button: HTMLInputElement): void {
    // Prepare data to send to Google Analytics
    const eventData = {
      event_category: 'Button Click',
      event_label: button.value || 'Unknown Button',
    };

    this.analyticsService.trackEvent('button_click', eventData);
  }

  private filterInlineProductsByLifeStage(): string {
    const doc = new DOMParser().parseFromString(this.bodyHtml, 'text/html');
    const products = doc.getElementsByClassName('ctaProductAndService');

    if (products?.length > 0) {
      Array.from(products).forEach((p) => {
        if (!p.classList.contains('hidden')) {
          let containsLifeStage = false;
          this.petLifeStages.forEach((stage) => {
            if (p.classList.contains(stage)) {
              containsLifeStage = true;
            }
          });

          if (!containsLifeStage) {
            p.className = p.className + ' hidden';
          }
        }
      });
    }

    return doc.body.innerHTML;
  }

  private getPetInfo(): void {
    this.daysSinceAdoption = this.petInfo.daysSinceAdoption;
    const months = this.articleUtilsService.calculatePetAgeInMonths(
      this.petInfo.dateOfBirth,
    );

    // Check for invalid date of birth and adjust categories accordingly
    if (this.petInfo.dateOfBirth === '0001-01-01T00:00:00' || isNaN(months)) {
      this.petLifeStages = [
        this.petInfo.animalType === 'Dog' ? 'speciesDogAll' : 'speciesCatAll',
      ];
    } else {
      this.petLifeStages = this.articleUtilsService.getCategoriesByAgeAndType(
        months,
        this.petInfo.animalType as 'Dog' | 'Cat',
      );
    }
  }

  private replaceBottomProductsPTags(): void {
    this.filteredProductCTAs.map((product: any) => {
      product.ctaTextHtml = this.replaceContent(product.ctaTextHtml);
    });
  }

  private replaceContent(text: string): string {
    const firstNameRegex = new RegExp('{{firstname}}', 'g');
    const lastNameRegex = new RegExp('{{lastname}}', 'g');
    const animalNameRegex = new RegExp('{{animalname}}', 'g');
    const animalSpeciesRegex = new RegExp('{{animalspecies}}', 'g');
    const shelterNameRegex = new RegExp('{{sheltername}}', 'g');
    const shelterShortNameRegex = new RegExp('{{sheltershortname}}', 'g');
    const shelterTypeOrgRegex = new RegExp('{{shelterorgtypeshort}}', 'g');

    text = text.replace(firstNameRegex, this.ownerInfo.firstname);
    text = text.replace(lastNameRegex, this.ownerInfo.lastname);
    text = text.replace(animalNameRegex, this.petInfo.name);
    text = text.replace(animalSpeciesRegex, this.petInfo.animalType);
    text = text.replace(shelterNameRegex, this.shelterInfo.businessName);
    text = text.replace(shelterShortNameRegex, this.shelterInfo.shortName);
    text = text.replace(shelterTypeOrgRegex, this.shelterInfo.category);

    return text;
  }

  private filterTeleAdviceInlineProducts(products: Element[]): void {
    const environment = this.config.environmentType;
    const guardianVetsBase =
      environment === 'production'
        ? 'https://petszel.guardianvets.com'
        : 'https://petszel-staging.guardianvets.com';

    const teleAdviceShelter =
      this.shelterInfo.features?.[this.teleAdviceFeature.feature] ?? true;

    products.forEach((p) => {
      const hasFeatureTag = Array.from(p.classList).some(
        (c) => c === this.teleAdviceTagId,
      );

      if (hasFeatureTag) {
        if (this.teleAdviceFeature.enabled && teleAdviceShelter) {
          const a = p.querySelector('a');
          if (a) {
            const base = `${guardianVetsBase}/?shelter_id=${this.shelterInfo.accountId}`;
            const href = this.withParams(
              base,
              this.buildUtmParams('Article', { Product: 'TeleAdvice' }),
            );
            a.setAttribute('href', href);
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener');
          }
        } else {
          this.renderer.addClass(p, 'hidden');
        }
      }
    });
  }

  private filterTeleAdviceProducts(products: ProductType[]): ProductType[] {
    const environment = this.config.environmentType;
    const guardianVetsBase =
      environment === 'production'
        ? 'https://petszel.guardianvets.com'
        : 'https://petszel-staging.guardianvets.com';

    const teleAdviceShelter =
      this.shelterInfo.features?.[this.teleAdviceFeature.feature] ?? true;

    return products
      .map((p: ProductType) => {
        const hasFeatureTag = p.systemMetadata?.tags.some(
          (tag: TagType) => tag.sys.id === this.teleAdviceTagId,
        );

        if (hasFeatureTag) {
          if (this.teleAdviceFeature.enabled && teleAdviceShelter) {
            return {
              ...p,
              ctaUrl1: `${guardianVetsBase}/?shelter_id=${this.shelterInfo.accountId}`,
            };
          }
          return undefined as any;
        }
        return p;
      })
      .filter((p): p is ProductType => p !== undefined);
  }

  private setShelter(shelters: ShelterExtendedModel[]): void {
    const selectedShelter =
      shelters.find(
        (s) => s.shelter.accountId === this.petInfo.shelterAccountId,
      )?.shelter || null;

    this.legalName =
      selectedShelter?.businessName ||
      selectedShelter?.legalName ||
      'Unknown Shelter';

    this.shelterBrandingDays =
      selectedShelter?.configuration.shelterBrandingDays || 0;

    if (selectedShelter) {
      this.shelterInfo = selectedShelter;
    }
  }
}
